"use client";

import { useEffect } from "react";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, RefreshCw, ArrowLeft } from "lucide-react";
import Link from "next/link";
import Image from "next/image";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log the error to an error reporting service
    logger.error("Worker profile error:", error);
  }, [error]);

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-14 max-w-screen-xl items-center justify-between px-4">
          <Link href="/dashboard" className="flex items-center gap-2">
            <Image src="/logo_black.svg" alt="RateRight" width={120} height={34} className="h-7 w-auto" />
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-screen-sm px-4 py-6">
        <Card className="mt-8">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle className="text-xl">Profile Build Error</CardTitle>
            <CardDescription className="mt-2">
              We couldn't load the profile builder. This might be a temporary issue.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {process.env.NODE_ENV === "development" && (
              <div className="rounded-lg bg-muted p-4">
                <p className="text-sm font-medium text-muted-foreground">Error details:</p>
                <p className="mt-1 text-sm text-destructive">{error.message}</p>
                {error.digest && (
                  <p className="mt-2 text-xs text-muted-foreground">Error ID: {error.digest}</p>
                )}
              </div>
            )}
            
            <div className="flex flex-col gap-3">
              <Button onClick={reset} className="w-full gap-2">
                <RefreshCw className="h-4 w-4" />
                Try again
              </Button>
              
              <Link href="/dashboard" className="w-full">
                <Button variant="outline" className="w-full gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Dashboard
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}